import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  Box,
  Tooltip,
} from "@mui/material";
import { useUsuario } from "../../../providers/usuarioProvider";
import { LoadingButton, TabContext, TabPanel } from "@mui/lab";
import Relatorio1 from "./relatorio1";
import Relatorio2 from "./relatorio2";
import Relatorio3ou4 from "./relatorio3ou4";
import Relatorio5 from "./relatorio5";
import Functions from "../../../callable/functions";
import { saveAs } from "file-saver";
import { IconArrowLeft, IconDownload } from "@tabler/icons-react";
import EmptyState from "../../util/ui/emptyState";
import Declaracao from "../../../models/declaracao";
import { onSnapshot } from "firebase/firestore";
import DeclaracaoDb from "../../../db/declaracaoDb";
import { useNavigate, useParams } from "react-router-dom";
import { radius } from "../../../theme";

export default function Relatorios() {
  const { declaracaoId } = useParams();
  const navigate = useNavigate();
  const { visualizado } = useUsuario();
  const [declaracao, setDeclaracao] = useState<Declaracao>();
  const [tab, setTab] = useState("Bens e Direitos");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!!visualizado && !!declaracaoId)
      return onSnapshot(
        new DeclaracaoDb().doc({ usuarioId: visualizado, declaracaoId }),
        (doc) => {
          if (doc.exists()) setDeclaracao(doc.data());
        }
      );
  }, [visualizado, declaracaoId]);

  return (
    <Container maxWidth="lg" sx={{ px: 8, py: 4 }}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={2}
      >
        <Box flexGrow={1}>
          <Typography variant="eyebrow" color="text.secondary">
            Declaração
          </Typography>
          {/* Ano-calendário em mono tabular — é dado, não texto. */}
          <Typography variant="h1" color="text.primary" pt={0.5} gutterBottom>
            Relatórios de Declaração{" "}
            <Typography
              component="span"
              variant="dataMono"
              sx={{ fontSize: "inherit", fontWeight: 500 }}
            >
              {declaracaoId}
            </Typography>
          </Typography>
        </Box>
        <Box m={1 / 2} />
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          startIcon={<IconArrowLeft />}
        >
          Voltar
        </Button>
        <Box m={1 / 2} />
        <LoadingButton
          loading={loading}
          onClick={() => {
            if (!!declaracao) {
              setLoading(true);
              new Functions()
                .declaracao({ data: declaracao.dataBase.format() })
                .then(({ data: url }) => {
                  saveAs(url);
                })
                .finally(() => setLoading(false));
            }
          }}
          startIcon={<IconDownload />}
          variant="contained"
          color="primary"
        >
          Baixar Relatório
        </LoadingButton>
      </Box>
      {declaracao ? (
        <>
          <TabContext value={tab}>
            <Tabs
              value={tab}
              onChange={(_event, value) => setTab(value)}
              // Metáfora de pasta: aba inativa em fill de tinta, ativa sobe
              // para `surface` com um filete accent-ink no topo.
              sx={{
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  color: "text.secondary",
                  backgroundColor: "action.hover",
                  border: 1,
                  borderColor: "divider",
                  borderBottom: "none",
                  borderTopLeftRadius: `${radius.lg}px`,
                  borderTopRightRadius: `${radius.lg}px`,
                  "&.Mui-selected": {
                    color: "text.primary",
                    backgroundColor: "background.paper",
                    boxShadow: (theme) =>
                      `inset 0 2px 0 ${theme.palette.accentInk}`,
                  },
                },
              }}
              variant="fullWidth"
              TabIndicatorProps={{ children: <span /> }}
            >
              <Tab label="Relatório 1" value={declaracao.relatorio1.nome} />
              <Tab label="Relatório 2" value={declaracao.relatorio2.nome} />
              <Tab label="Relatório 3" value={declaracao.relatorio3.nome} />
              <Tab label="Relatório 4" value={declaracao.relatorio4.nome} />
              <Tab label="Relatório 5" value={declaracao.relatorio5.nome} />
            </Tabs>
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: `${radius.xl}px`,
                borderBottomRightRadius: `${radius.xl}px`,
              }}
            >
              <Tooltip
                title="Clique sobre os valores para copiar para sua área de transferência e cole no programa da Receita."
                sx={{ fontSize: 14 }}
                placement="bottom"
              >
                <>
                  <TabPanel value={declaracao.relatorio1.nome}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      align="center"
                      color="text.primary"
                    >
                      {declaracao.relatorio1.nome}
                    </Typography>

                    <Relatorio1
                      relatorio={declaracao.relatorio1}
                      dataBase={declaracao.dataBase}
                    />
                  </TabPanel>
                  <TabPanel value={declaracao.relatorio2.nome}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      align="center"
                      color="text.primary"
                    >
                      {declaracao.relatorio2.nome}
                    </Typography>

                    <Relatorio2
                      relatorio={declaracao.relatorio2}
                      dataBase={declaracao.dataBase}
                    />
                  </TabPanel>
                  <TabPanel value={declaracao.relatorio3.nome}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      align="center"
                      color="text.primary"
                    >
                      {declaracao.relatorio3.nome}
                    </Typography>

                    <Relatorio3ou4 relatorio={declaracao.relatorio3} />
                  </TabPanel>
                  <TabPanel value={declaracao.relatorio4.nome}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      align="center"
                      color="text.primary"
                    >
                      {declaracao.relatorio4.nome}
                    </Typography>

                    <Relatorio3ou4 relatorio={declaracao.relatorio4} />
                  </TabPanel>
                  <TabPanel value={declaracao.relatorio5.nome}>
                    <Typography
                      variant="h3"
                      gutterBottom
                      align="center"
                      color="text.primary"
                    >
                      {declaracao.relatorio5.nome}
                    </Typography>

                    <Relatorio5 relatorio={declaracao.relatorio5} />
                  </TabPanel>
                </>
              </Tooltip>
            </Paper>
          </TabContext>
        </>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          width="100%"
          paddingX={4}
        >
          <EmptyState maxHeight="50%" maxWidth="80%" />
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ padding: 2 }}
          >
            Ainda não há declaração calculada.
          </Typography>
        </Box>
      )}
    </Container>
  );
}
